import React, { Fragment, Suspense, useEffect } from "react";
import { connect } from "react-redux";

// Layout Related
import { Container } from "reactstrap";
import AppNavbar from "./AppNavbar";
import Sidebar from "./AppSidebar";
import classnames from "classnames";
import { PacmanLoader } from "react-spinners";
import UserNotifications from "./UserNotifications";

import { getCurrentUser, addNotification } from "modules/current_user/actions";
import "./AppLayout.css";

// Router related
import routes from "modules/2_routes/web";
import { matchPath, Route, Switch, useLocation,useParams } from "react-router-dom";

// Extras
import ErrorsDisplay from "./ErrorsDisplay";
import { Toaster } from "react-hot-toast";
import { Button } from "reactstrap";

import qs from "query-string";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompress, faExpand } from "@fortawesome/free-solid-svg-icons";
import HasRole from "modules/5_view_helpers/HasRole";
import { getProject } from "modules/projects/actions";
import { toggleFullscreen } from "modules/3_reducers/web/ThemeOptions";
import HistorySidebar from "modules/5_view_helpers/web/HistorySliding/HistorySidebar";

function mapStateToProps(state) {
  return {
    pending: state.pending,
    current_user: state.current_user,
    custom_forms:state.custom_forms,
  };
}

const Preloader = () => {
  return (
    <div className="centered">
      <PacmanLoader />
    </div>
  );
};

const AppLayout = (props) => {
  const {
    forceFullscreen,
    fullscreenEnabled,
    location: { pathname, search },
    pending,
    current_user,
    addNotification,
    toggleFullscreen,
    custom_forms,
  } = props;
  const [showMenu, setShowMenu] = React.useState(false);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [actualMatch, setActualMatch] = React.useState(null);

  const query = qs.parse(search);
  const forced = forceFullscreen || query.fullscreen == "true";
  const fullscreen = fullscreenEnabled || forced;

  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    props.getCurrentUser(location.pathname);
  }, [location])
  

  useEffect(() => {
    if (fullscreen) {
      const readRole = HasRole({
        resource_type: "Project",
        name: ["read", "moderator"],
      });
      if (readRole) {
        props.getProject(current_user?.current_project_id);
      }
    }
  }, [fullscreen, current_user]);

  let locationType = "main";
  if (_.includes(pathname, "production")) {
    locationType = "production";
  }
  if ((_.includes(pathname, "/qa") || _.includes(pathname, "assemblies")) && query?.return_page != "project") {
    locationType = "qa";
  }
  if (_.includes(pathname, "hs")) {
    locationType = "hs";
  }
  if (_.includes(pathname, "store")) {
    locationType = "store";
  }
  if (_.includes(pathname, "drawing_office") || _.includes(pathname, "site_document_releases") || _.includes(pathname, "drawings")) {
    locationType = "drawing_office";
  }
  if( /^\/custom_forms\/menus\/\d+\/module$/.test(pathname)){
    locationType = "custom_forms_menu_module";
  }

  // Check if the path has a custom forms menu module
  const match = matchPath(window.location.pathname, {
    path: "/custom_form/:custom_form_id/records",
    exact: true,
  });
  // If your on a custom form location then get the custom form id and check if it has a menu_id then set the locationType to custom_forms_menu_module
  if (match) {
    const custom_form = custom_forms[match?.params?.custom_form_id];
    if(custom_form?.menu_id){
      locationType = "custom_forms_menu_module";
    }
  }


  if (_.isEmpty(current_user)) {
    return <div>Loading user...</div>;
  }

  return (
    <Container fluid>
      {!fullscreen && (
        <AppNavbar
          setShowMenu={setShowMenu}
          setShowNotifications={setShowNotifications}
          actualMatch={actualMatch}
          locationType={locationType}
        />
      )}

      <div className="layout">
        {!fullscreen && (
          <div className="sidebar">
            <Sidebar showMenu={showMenu} setShowMenu={setShowMenu} locationType={locationType} />
          </div>
        )}
        <div
          className={classnames({
            "content-fullscreen": fullscreen,
            content: !fullscreen,
          })}
        >
          <ErrorsDisplay />

          {/* Router */}
          <Suspense fallback={<Preloader />}>
            <Switch>
              {_.map(routes, (route, i) => {
                return (
                  <Route
                    key={i}
                    path={route.path}
                    exact={route.exact}
                    name={route.name}
                    render={(props) => (
                      <RouteRenderer
                        setActualMatch={setActualMatch}
                        forceFullscreen={forced}
                        fullscreen={fullscreen}
                        pending={pending}
                        route={route}
                        toggleFullscreen={() => {
                          toggleFullscreen(!fullscreen);
                        }}
                        {...props}
                      />
                    )}
                  />
                );
              })}
            </Switch>
          </Suspense>
          {/* Router */}
          <Toaster position="top-center" />
          <UserNotifications open={showNotifications} setOpen={setShowNotifications} />
          <HistorySidebar />
        </div>
        <div id="RightSidebarRoot"></div>
      </div>
    </Container>
  );
};

// export default AppLayout;
export default connect(mapStateToProps, { getCurrentUser, addNotification, getProject, toggleFullscreen })(AppLayout);

const RouteRenderer = (props) => {
  const {
    match: { path, params },
    match,
    pending,
    route,
    setActualMatch,
    fullscreen,
    forceFullscreen,
    toggleFullscreen,
  } = props;

  useEffect(() => {
    setActualMatch(match);
  }, []);

  // Check if the path needs a project_id
  let needs_project = _.includes(path, ":project_id") && !_.isInteger(parseInt(params?.project_id));
  // special case for project show.
  if (!needs_project) {
    needs_project = _.includes(path, "/projects/:id") && !_.isInteger(parseInt(params?.id));
  }

  return (
    <Fragment>
      {!forceFullscreen && (
        <div className="text-right">
          <FontAwesomeIcon className="pr-2" icon={fullscreen ? faCompress : faExpand} onClick={toggleFullscreen} style={{ cursor: "pointer" }} />
        </div>
      )}
      <div style={{ position: "relative" }}>
        {pending != 0 && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              background: "rgba(255,255,255,0.65)",
            }}
          >
            <PacmanLoader />
          </div>
        )}
        {needs_project ? <div>No project selected.</div> : <route.component {...props} />}
      </div>
    </Fragment>
  );
  yz;
};

const UserNotification = (data) => {
  const { message, notification_type, link, link_text, link_new_tab } = data;

  const duration = 5000;

  if (notification_type == "error" || notification_type == "success") {
    if (link) {
      toastr[notification_type](
        <Fragment>
          {message}
          <a href={link} target={link_new_tab ? "_blank" : null}>
            <Button className="ml-1" color="success">
              {link_text}
            </Button>
          </a>
        </Fragment>,
        { duration: duration }
      );
    } else {
      toastr[notification_type](message, { duration: duration });
    }
  } else {
    toastr(message, { duration: duration });
  }
};
